import { Button, DotsHorizontalIcon, Dropdown, DropdownMenu, DropdownMenuItem } from '@bp/ui-components';
import { Row } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useTimetableStore } from '../../../TimetableGrid/TimetableProvider';
import { VersionLessonTableType } from '../TimetableVersionLessonStore';

type TimetableVersionLessonsTableLastColProps = {
  row: Row<VersionLessonTableType>;
  handleEdit: (row: Row<VersionLessonTableType>) => void;
  setCurrentLessonUuid: (uuid: string | null) => void;
  setLessonCardsModalOpen: (open: boolean) => void;
  handleDuplicate: (lessons: VersionLessonTableType[]) => Promise<void>;
  handleAssign: (lesson: VersionLessonTableType, teachers: { uuid: string; selectName?: string }[]) => void;
  handleDetach: (uuids: string[]) => void;
  updateCheckboxes: (params: {
    type: 'teachingLoadEnabled' | 'timetableEnabled' | 'elective';
    lessonUuid: string;
    value: boolean;
  }) => Promise<void>;
  handleDelete: (lessons: VersionLessonTableType[]) => void;
  handleDeactivate: (lessons: VersionLessonTableType[]) => Promise<void>;
};

export const TimetableVersionLessonsTableLastCol = observer(
  ({
    row,
    handleEdit,
    handleAssign,
    handleDetach,
    handleDuplicate,
    setCurrentLessonUuid,
    setLessonCardsModalOpen,
    updateCheckboxes,
    handleDelete,
    handleDeactivate,
  }: TimetableVersionLessonsTableLastColProps) => {
    const { t } = useTranslation();

    const { teacherData } = useLoadBasicData({ pause: false });
    const store = useTimetableStore();

    const inVersionLessonDropdownItems = (row: Row<VersionLessonTableType>): DropdownMenuItem[] => {
      const qualified = teacherData?.people.filter(
        (person) =>
          person.qualifications.some((q) => q.subject.uuid === row.original.subjectUuid) &&
          !row.original.teacherUuids.includes(person.uuid),
      );
      const teachers = qualified?.length
        ? qualified
        : teacherData?.people.filter((person) => !row.original.teacherUuids.includes(person.uuid));

      return [
        {
          label: t('lesson.edit'),
          disabled: store.readonly,
          onClick: () => {
            handleEdit(row);
          },
        },
        {
          label: t('timetableVersion.editCards'),
          disabled: store.readonly,

          onClick: () => {
            setCurrentLessonUuid(row.original.uuid);
            setLessonCardsModalOpen(true);
          },
        },
        {
          label: t('common.duplicate'),
          disabled: store.readonly,

          onClick: async () => {
            await handleDuplicate([row.original]);
          },
        },
        {
          label: t('lesson.actions.assignTo'),
          disabled: store.readonly || row.original.isSubjectContainerLesson,
          subContent: teachers
            ? teachers.map((teacher) => {
                return {
                  type: 'default',
                  label: teacher.selectName ?? '',
                  onClick: () => {
                    handleAssign(row.original, [teacher]);
                  },
                };
              })
            : [],
        },
        {
          label: t('lesson.actions.detach'),
          disabled: store.readonly || row.original.isSubjectContainerLesson,

          onClick: () => {
            handleDetach([row.original.uuid]);
          },
        },
        {
          type: 'ruler',
        },
        {
          label: t('lesson.actions.toggle.isElective'),
          type: 'switch',
          value: row.original.elective ?? false,
          disabled: store.readonly || row.original.isSubjectContainerLesson,

          onValueChange: async () => {
            await updateCheckboxes({ type: 'elective', lessonUuid: row.original.uuid, value: !row.original.elective });
          },
        },
        {
          label: t('lesson.basics.isTeachingLoadEnabled.full'),
          type: 'switch',
          disabled: store.readonly || row.original.isSubjectContainerLesson,
          value: row.original.teachingLoadEnabled ?? false,
          onValueChange: async () => {
            await updateCheckboxes({
              type: 'teachingLoadEnabled',
              lessonUuid: row.original.uuid,
              value: !row.original.teachingLoadEnabled,
            });
          },
        },
        {
          label: t('lesson.basics.isTimetableEnabled.full'),
          type: 'switch',
          disabled: true,
          value: row.original.timetableEnabled ?? false,
          onValueChange: async () => {
            await updateCheckboxes({
              type: 'timetableEnabled',
              lessonUuid: row.original.uuid,
              value: !row.original.timetableEnabled,
            });
          },
        },
        {
          type: 'ruler',
        },
        {
          label: t('common.delete'),
          type: 'default',
          color: 'error',
          disabled: store.readonly,
          onClick: () => {
            handleDelete([row.original]);
          },
        },
      ];
    };
    const inVersionTeachingBlockDropdownItems = (row: Row<VersionLessonTableType>): DropdownMenuItem[] => {
      return [
        {
          label: t('lesson.edit'),
          disabled: store.readonly,
          onClick: () => {
            handleEdit(row);
          },
        },
        {
          label: t('timetableVersion.editCards'),
          disabled: store.readonly,

          onClick: () => {
            setCurrentLessonUuid(row.original.uuid);
            setLessonCardsModalOpen(true);
          },
        },
        {
          type: 'ruler',
        },
        {
          label: t('common.delete'),
          type: 'default',
          color: 'error',
          disabled: store.readonly,
          onClick: () => {
            handleDelete([row.original]);
          },
        },
      ];
    };

    const defaultDropdownItems = (row: Row<VersionLessonTableType>): DropdownMenuItem[] => [
      {
        label: t('timetableVersion.editInVersion'),
        onClick: async () => {
          await handleDuplicate([row.original]);
        },
      },
      {
        label: t('timetableVersion.editCards'),
        onClick: () => {
          setCurrentLessonUuid(row.original.uuid);
          setLessonCardsModalOpen(true);
        },
      },
      {
        label: t('timetableVersion.deactivate'),
        type: 'default',
        color: 'error',
        onClick: async () => {
          await handleDeactivate([row.original]);
        },
      },
    ];

    return (
      <>
        {row.original.onlyInTimetableVersion && !row.original.isSubjectContainerLesson ? (
          <Dropdown
            disabled={store.readonly}
            trigger={
              <Button disabled={store.readonly} hierarchy='secondary' icon={<DotsHorizontalIcon className='small' />} />
            }
          >
            <DropdownMenu data={inVersionLessonDropdownItems(row)} />
          </Dropdown>
        ) : row.original.onlyInTimetableVersion && row.original.isSubjectContainerLesson ? (
          <Dropdown
            disabled={store.readonly}
            trigger={
              <Button disabled={store.readonly} hierarchy='secondary' icon={<DotsHorizontalIcon className='small' />} />
            }
          >
            <DropdownMenu data={inVersionTeachingBlockDropdownItems(row)} />
          </Dropdown>
        ) : (
          <Dropdown
            disabled={store.readonly}
            trigger={
              <Button disabled={store.readonly} hierarchy='secondary' icon={<DotsHorizontalIcon className='small' />} />
            }
          >
            <DropdownMenu data={defaultDropdownItems(row)} />
          </Dropdown>
        )}
      </>
    );
  },
);
